import { Geometry, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from './ll/models/geometry';

type Attrs = Record<string, string>;

const encode = (s: string) => `${s}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const attr = (obj: Attrs | undefined) => obj ? (' ' + Object.entries(obj).map(([k, v]) => k + '="' + encode(v) + '"').join(' ')) : '';

const tag = (el: string, contents: string, attributes?: Attrs) => '<' + el + attr(attributes) + '>' + contents + '</' + el + '>';

interface Options {
    documentName?: string;
    documentDescription?: string;
    name: string;
    description: string;
}

const toHex = (x: number, pad: number) => x.toString(16).padStart(pad, '0');

const color = (red: number, green: number, blue: number, alpha: number) =>
    toHex(Math.floor(255 * alpha), 2) + toHex(blue, 2) + toHex(green, 2) + toHex(red, 2);

export const toKml = (geojson: { geometry: Geometry }, options?: Partial<Options>) => {
    options = options || {};
    const opts: Options = {
        ...options,
        name: options.name || 'name',
        description: options.description || 'description',
    };

    const lineStyle = tag('LineStyle', tag('color', color(255, 0, 0, 0.4)) + tag('width', '5'));
    const polyStyle = tag('PolyStyle', tag('color', color(255, 0, 0, 0.15)));

    const style = lineStyle + polyStyle;

    return '<?xml version="1.0" encoding="UTF-8"?>' +
        tag(
            'kml',
            tag('Document',
                (options.documentName ? tag('name', options.documentName) : '') +
                (options.documentDescription ? tag('description', options.documentDescription) : '') +
                tag('Style', style, { 'id': 'parcel' }) +
                root(geojson, opts)
            ),
            { 'xmlns': 'http://www.opengis.net/kml/2.2' }
        );
};


const linearring = (coords: any[]) => coords.map(cds => cds.join(',')).join(' ');

const geometry = {
    Point: (g: Point) => tag('Point', tag('coordinates', g.coordinates.join(','))),
    LineString: (g: LineString) => tag('LineString', tag('coordinates', linearring(g.coordinates))),
    Polygon: (g: Polygon) => {
        if (!g.coordinates.length) {
            return '';
        }
        const outerRing = tag('outerBoundaryIs', tag('LinearRing', tag('coordinates', linearring(g.coordinates[0]))));
        const innerRings = g.coordinates.slice(1).map(r =>
            tag('innerBoundaryIs', tag('LinearRing', tag('coordinates', linearring(r))))
        ).join('');
        return tag('Polygon', outerRing + innerRings);
    },
    MultiPoint: (g: MultiPoint) => {
        if (!g.coordinates.length) {
            return '';
        }
        return tag('MultiGeometry', g.coordinates.map(c => geometry.Point({ type: 'Point', coordinates: c })).join(''));
    },
    MultiPolygon: (g: MultiPolygon) => {
        if (!g.coordinates.length) {
            return '';
        }
        return tag('MultiGeometry', g.coordinates.map(c => geometry.Polygon({ type: 'Polygon', coordinates: c })).join(''));
    },
    MultiLineString: (g: MultiLineString) => {
        if (!g.coordinates.length) {
            return '';
        }
        return tag('MultiGeometry', g.coordinates.map(c => geometry.LineString({ type: 'LineString', coordinates: c })).join(''));
    },
};

const data = ([k, v]: [string, string]) => tag('Data', tag('value', encode(v)), { 'name': k });

const extendeddata = (d: Attrs) => tag('ExtendedData', Object.entries(d).map(data).join(''));


const feature = (options: Options) => (f: any) => {
    if (!f.properties || !f.geometry) {
        return '';
    }
    const geometryString = geometry[f.geometry.type as keyof typeof geometry](f.geometry);
    if (!geometryString) {
        return '';
    }
    return tag('Placemark',
        (f.properties[options.name] ? tag('name', encode(f.properties[options.name])) : '') +
        (f[options.description] ? tag('description', encode(f[options.description])) : '') +
        extendeddata(f.properties) +
        geometryString +
        tag('styleUrl', '#parcel'));
};


function root(geojson: any, options: Options) {
    if (!geojson.type) {
        return '';
    }

    switch (geojson.type) {
        case 'FeatureCollection':
            if (!geojson.features) {
                return '';
            }
            return geojson.features.map(feature(options)).join('');
        case 'Feature':
            return feature(options)(geojson);
        default:
            return feature(options)({
                type: 'Feature',
                geometry: geojson,
                properties: {}
            });
    }
}
